(() => {
  'use strict';

  if (window.__watcFreeGiftNoticeLoaded) return;
  window.__watcFreeGiftNoticeLoaded = true;

  const DISPLAY_TIME = 5000;
  const REFRESH_DELAY = 300;
  const GIFT_UPDATED_EVENT = 'watc:free-gift-updated';

  let hideTimer = null;
  let noticeElement = null;
  let modalElement = null;
  let refreshRequest = 0;

  const nativeFetch = window.fetch.bind(window);


  /* ==========================================
     ROUTES
     ========================================== */

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function getCartUrl() {
    return `${getRootUrl()}cart`;
  }

  function isCartPage() {
    try {
      const currentPath = window.location.pathname.replace(/\/+$/, '');
      const cartPath = new URL(
        getCartUrl(),
        window.location.origin
      ).pathname.replace(/\/+$/, '');

      return currentPath === cartPath;
    } catch (error) {
      return false;
    }
  }


  /* ==========================================
     CREATE NOTICE
     ========================================== */

  function createNotice() {
    if (noticeElement && document.body.contains(noticeElement)) {
      return noticeElement;
    }

    noticeElement = document.createElement('div');
    noticeElement.className = 'watc-gift-notice';

    noticeElement.setAttribute('role', 'status');
    noticeElement.setAttribute('aria-live', 'polite');

    noticeElement.innerHTML = `
      <div class="watc-gift-notice__inner">

        <p class="watc-gift-notice__message"></p>

        <a
          class="watc-gift-notice__action"
          href="${getCartUrl()}"
          hidden
        >
          Go to basket to choose →
        </a>

        <button
          type="button"
          class="watc-gift-notice__close"
          aria-label="Dismiss complimentary gift message"
        >
          ×
        </button>

      </div>
    `;

    document.body.appendChild(noticeElement);

    const closeButton = noticeElement.querySelector(
      '.watc-gift-notice__close'
    );

    closeButton?.addEventListener('click', hideNotice);

    return noticeElement;
  }


  /* ==========================================
     CREATE FREE-GIFT MODAL
     ========================================== */

  function createGiftModal() {
    if (modalElement && document.body.contains(modalElement)) {
      return modalElement;
    }

    modalElement = document.createElement('dialog');
    modalElement.className = 'watc-gift-modal';
    modalElement.setAttribute('aria-modal', 'true');

    modalElement.innerHTML = `
      <div class="watc-gift-modal__panel">

        <button
          type="button"
          class="watc-gift-modal__close"
          aria-label="Close complimentary gift selection"
        >
          ×
        </button>

        <div class="watc-gift-modal__content"></div>

      </div>
    `;

    document.body.appendChild(modalElement);

    modalElement
      .querySelector('.watc-gift-modal__close')
      ?.addEventListener('click', closeGiftModal);

    /*
     * With a native dialog, a click on the shaded backdrop
     * targets the dialog itself rather than the inner panel.
     */
    modalElement.addEventListener('click', (event) => {
      if (event.target === modalElement) {
        closeGiftModal();
      }
    });

    modalElement.addEventListener('close', () => {
      document.body.classList.remove('watc-gift-modal-open');
    });

    return modalElement;
  }


  function closeGiftModal() {
    if (!modalElement) return;

    if (modalElement.open) {
      modalElement.close();
    }

    document.body.classList.remove('watc-gift-modal-open');
  }


  function showGiftModal(selectorHtml) {
    if (!selectorHtml) return;

    const modal = createGiftModal();
    const content = modal.querySelector('.watc-gift-modal__content');

    if (!content) return;

    const parsedDocument = new DOMParser().parseFromString(
      selectorHtml,
      'text/html'
    );

    const selector = parsedDocument.querySelector(
      'watc-free-gift-selector'
    );

    if (!selector) return;

    content.replaceChildren(selector);

    const heading = selector.querySelector(
      '.watc-free-gift__heading'
    );

    if (heading) {
      heading.id = 'watc-gift-modal-title';
      modal.setAttribute('aria-labelledby', heading.id);
    } else {
      modal.setAttribute(
        'aria-label',
        'Choose your complimentary gift'
      );
    }

    hideNotice();
    document.body.classList.add('watc-gift-modal-open');

    if (!modal.open) {
      modal.showModal();
    }

    modal
      .querySelector('.watc-gift-modal__close')
      ?.focus({ preventScroll: true });
  }


  /* ==========================================
     POSITION UNDER HEADER / NAV
     ========================================== */

  function calculateNoticeTop() {
    if (!noticeElement) return;

    const selectors = [
      '.shopify-section-group-header-group',
      '#shopify-section-header',
      'header-component',
      'header'
    ];

    const elements = document.querySelectorAll(
      selectors.join(',')
    );

    let lowestVisiblePoint = 0;

    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();

      /*
       * Ignore invisible, off-screen or unusually large
       * header-like elements.
       */
      if (
        rect.height <= 0 ||
        rect.height > 350 ||
        rect.bottom <= 0 ||
        rect.top > 350
      ) {
        return;
      }

      lowestVisiblePoint = Math.max(
        lowestVisiblePoint,
        rect.bottom
      );
    });

    /*
     * Don't let a weird theme element push the
     * message halfway down the screen.
     */
    lowestVisiblePoint = Math.min(
      Math.max(lowestVisiblePoint, 0),
      350
    );

    noticeElement.style.setProperty(
      '--watc-gift-notice-top',
      `${Math.round(lowestVisiblePoint)}px`
    );
  }


  /* ==========================================
     SHOW / HIDE
     ========================================== */

  function hideNotice() {
    if (!noticeElement) return;

    noticeElement.classList.remove('is-visible');

    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  }


  function showNotice(state) {
    if (
      !state ||
      isCartPage() ||
      state.threeForTwoActive ||
      state.otherProductDiscountActive ||
      state.eligible
    ) {
      return;
    }

    const notice = createNotice();

    const message = notice.querySelector(
      '.watc-gift-notice__message'
    );

    const action = notice.querySelector(
      '.watc-gift-notice__action'
    );

    hideNotice();

    notice.classList.remove('is-unlocked');

    /* ------------------------------------------
       STILL BELOW £75
       ------------------------------------------ */

    if (!state.amountRemainingMoney) {
      return;
    }

    message.textContent =
      `🎁 You’re ${state.amountRemainingMoney} away from a complimentary gift`;

    action.hidden = true;

    calculateNoticeTop();

    /*
     * Force the browser to register the hidden
     * state before animating it in.
     */
    requestAnimationFrame(() => {

      requestAnimationFrame(() => {

        calculateNoticeTop();

        notice.classList.add('is-visible');

      });

    });


    hideTimer = window.setTimeout(
      hideNotice,
      DISPLAY_TIME
    );
  }


  /* ==========================================
     READ LIQUID FREE-GIFT STATE
     ========================================== */

  function stateFromSelector(selector) {
    if (!selector) return null;

    return {
      eligible:
        selector.dataset.eligible === 'true',

      threeForTwoActive:
        selector.dataset.threeForTwoActive === 'true',

      otherProductDiscountActive:
        selector.dataset.otherProductDiscountActive === 'true',

      amountRemaining:
        Number(
          selector.dataset.amountRemaining || 0
        ),

      amountRemainingMoney:
        selector.dataset.amountRemainingMoney || ''
    };
  }


  function snapshotFromSelector(selector) {
    if (!selector) return null;

    return {
      state: stateFromSelector(selector),
      selectorHtml: selector.outerHTML
    };
  }


  /* ==========================================
     GET FRESH STATE AND SELECTOR FROM SHOPIFY
     ========================================== */

  async function getFreshGiftSnapshot() {

    const currentSelector = document.querySelector(
      'watc-free-gift-selector[data-section-id]'
    );

    if (!currentSelector) {
      return null;
    }

    const sectionId =
      currentSelector.dataset.sectionId;

    if (!sectionId) {
      return snapshotFromSelector(currentSelector);
    }

    try {

      const url = new URL(
        getRootUrl(),
        window.location.origin
      );

      url.searchParams.set(
        'sections',
        sectionId
      );

      const response = await nativeFetch(
        url.toString(),
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `Gift section request failed: ${response.status}`
        );
      }

      const sections = await response.json();

      const html = sections?.[sectionId];

      if (!html) {
        return snapshotFromSelector(currentSelector);
      }

      const parsedDocument =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const refreshedSelector =
        parsedDocument.querySelector(
          'watc-free-gift-selector'
        );

      return snapshotFromSelector(
        refreshedSelector || currentSelector
      );

    } catch (error) {

      /*
       * If section rendering ever fails, fall
       * back to the selector already in the page.
       */
      return snapshotFromSelector(currentSelector);

    }
  }


  function showGiftExperience(snapshot) {
    const state = snapshot?.state;

    if (
      !state ||
      isCartPage() ||
      state.threeForTwoActive ||
      state.otherProductDiscountActive
    ) {
      closeGiftModal();
      hideNotice();
      return;
    }

    if (state.eligible) {
      showGiftModal(snapshot.selectorHtml);
      return;
    }

    closeGiftModal();
    showNotice(state);
  }


  /* ==========================================
     DISPLAY USING FRESH CART STATE
     ========================================== */

  async function refreshAndShowNotice() {

    if (isCartPage()) return;

    const thisRequest = ++refreshRequest;

    /*
     * Allow Shopify/theme cart updates a moment
     * to complete before requesting the section.
     */
    await new Promise((resolve) => {
      window.setTimeout(resolve, REFRESH_DELAY);
    });

    const snapshot = await getFreshGiftSnapshot();

    /*
     * Prevent an older request from overwriting
     * a newer add-to-cart action.
     */
    if (thisRequest !== refreshRequest) return;

    showGiftExperience(snapshot);
  }


  /* ==========================================
     IDENTIFY CART ADD REQUESTS
     ========================================== */

  function isCartAddUrl(input) {
    try {

      const rawUrl =
        typeof input === 'string'
          ? input
          : input?.url || '';

      const url = new URL(
        rawUrl,
        window.location.origin
      );

      return /\/cart\/add(?:\.js)?$/.test(
        url.pathname
      );

    } catch (error) {
      return false;
    }
  }


  /* ==========================================
     DON'T TRIGGER WHEN OUR FREE GIFT
     ITSELF IS ADDED
     ========================================== */

  function addedResponseIsOnlyGift(payload) {

    if (!payload) return false;

    const items = Array.isArray(payload.items)
      ? payload.items
      : [payload];

    if (!items.length) return false;

    return items.every((item) => {

      const marker =
        item?.properties?._watc_free_gift;

      return String(marker) === 'true';

    });
  }


  /* ==========================================
     INTERCEPT FETCH ADD-TO-CART
     ========================================== */

  window.fetch = async function (...args) {

    const isCartAdd =
      isCartAddUrl(args[0]);

    const response =
      await nativeFetch(...args);

    if (
      isCartAdd &&
      response.ok
    ) {

      let giftOnly = false;

      try {

        const payload =
          await response.clone().json();

        giftOnly =
          addedResponseIsOnlyGift(payload);

      } catch (error) {
        giftOnly = false;
      }

      if (!giftOnly) {

        try {
          sessionStorage.removeItem(
            'watcGiftNoticePending'
          );
        } catch (error) {}

        refreshAndShowNotice();

      }
    }

    return response;
  };


  /* ==========================================
     SUPPORT THEMES USING XMLHttpRequest
     ========================================== */

  const originalXhrOpen =
    XMLHttpRequest.prototype.open;

  const originalXhrSend =
    XMLHttpRequest.prototype.send;


  XMLHttpRequest.prototype.open =
    function (
      method,
      url,
      ...rest
    ) {

      this.__watcIsCartAdd =
        isCartAddUrl(url);

      return originalXhrOpen.call(
        this,
        method,
        url,
        ...rest
      );
    };


  XMLHttpRequest.prototype.send =
    function (body) {

      if (this.__watcIsCartAdd) {

        this.addEventListener(
          'load',
          function () {

            if (
              this.status < 200 ||
              this.status >= 300
            ) {
              return;
            }

            let giftOnly = false;

            try {

              const payload =
                JSON.parse(this.responseText);

              giftOnly =
                addedResponseIsOnlyGift(
                  payload
                );

            } catch (error) {
              giftOnly = false;
            }

            if (!giftOnly) {

              try {
                sessionStorage.removeItem(
                  'watcGiftNoticePending'
                );
              } catch (error) {}

              refreshAndShowNotice();

            }

          },
          { once: true }
        );
      }

      return originalXhrSend.call(
        this,
        body
      );
    };


  /* ==========================================
     SUPPORT NORMAL PRODUCT FORM SUBMISSIONS
     ========================================== */

  document.addEventListener(
    'submit',
    (event) => {

      const form =
        event.target.closest?.(
          'form[action*="/cart/add"]'
        );

      if (!form) return;

      const giftMarker =
        form.querySelector(
          '[name="properties[_watc_free_gift]"]'
        );

      if (
        giftMarker &&
        String(giftMarker.value) === 'true'
      ) {
        return;
      }

      /*
       * If the theme performs a full page
       * submission rather than AJAX, remember
       * that we need to show the notice after
       * the next page loads.
       */
      try {

        sessionStorage.setItem(
          'watcGiftNoticePending',
          'true'
        );

      } catch (error) {}

    },
    true
  );


  /* ==========================================
     FULL-PAGE SUBMISSION FALLBACK
     ========================================== */

  function checkPendingNotice() {

    if (isCartPage()) {

      try {
        sessionStorage.removeItem(
          'watcGiftNoticePending'
        );
      } catch (error) {}

      return;
    }

    try {

      const pending =
        sessionStorage.getItem(
          'watcGiftNoticePending'
        );

      if (pending === 'true') {

        sessionStorage.removeItem(
          'watcGiftNoticePending'
        );

        refreshAndShowNotice();
      }

    } catch (error) {}
  }


  /* ==========================================
     KEEP BAR UNDER STICKY HEADER
     ========================================== */

  let positionFrame = null;

  function schedulePositionUpdate() {

    if (
      !noticeElement ||
      !noticeElement.classList.contains(
        'is-visible'
      )
    ) {
      return;
    }

    if (positionFrame) return;

    positionFrame =
      requestAnimationFrame(() => {

        calculateNoticeTop();

        positionFrame = null;

      });
  }


  window.addEventListener(
    'resize',
    schedulePositionUpdate,
    { passive: true }
  );

  window.addEventListener(
    'scroll',
    schedulePositionUpdate,
    { passive: true }
  );


  /* ==========================================
     MODAL DISMISSAL AND SUCCESS EVENTS
     ========================================== */

  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Escape' &&
      modalElement?.open
    ) {
      event.preventDefault();
      closeGiftModal();
    }
  });

  document.addEventListener(
    GIFT_UPDATED_EVENT,
    closeGiftModal
  );


  /* ==========================================
     INITIALISE
     ========================================== */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      checkPendingNotice
    );

  } else {

    checkPendingNotice();

  }

  /* Compact free-gift button in the cart drawer. */
  const drawerGiftSelector =
    'cart-drawer-component watc-free-gift-selector[data-section-id]';

  function updateDrawerGiftLink() {
    const selector = document.querySelector(drawerGiftSelector);
    if (!selector) return;

    const state = stateFromSelector(selector);
    const eligible = Boolean(
      state?.eligible &&
      !state.threeForTwoActive &&
      !state.otherProductDiscountActive
    );

    let link = selector.previousElementSibling;
    if (!link?.classList.contains('watc-gift-drawer-link')) link = null;

    if (!eligible) {
      selector.classList.remove('watc-gift-drawer-selector--compact');
      link?.remove();
      return;
    }

    selector.classList.add('watc-gift-drawer-selector--compact');

    if (!link) {
      link = document.createElement('button');
      link.type = 'button';
      link.className = 'watc-gift-drawer-link';
      selector.before(link);
    }

    const selected = Boolean(selector.dataset.selectedGiftKey?.trim());
    const labelState = selected ? 'selected' : 'unselected';

    if (link.dataset.giftState !== labelState) {
      link.dataset.giftState = labelState;
      link.innerHTML = selected
        ? '<span class="watc-gift-drawer-link__action">Click here to change your free gift <span aria-hidden="true">→</span></span>'
        : '<span class="watc-gift-drawer-link__title">Your order qualifies for a free gift</span><span class="watc-gift-drawer-link__action">Click here to select your free gift <span aria-hidden="true">→</span></span>';
    }
  }

  async function getFreshDrawerGiftSnapshot(selector) {
    const sectionId = selector.dataset.sectionId;
    if (!sectionId) return snapshotFromSelector(selector);

    try {
      const url = new URL(getRootUrl(), window.location.origin);
      url.searchParams.set('sections', sectionId);

      const response = await nativeFetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error('Gift section request failed');

      const sections = await response.json();
      const html = sections?.[sectionId];
      const refreshed = html
        ? new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('watc-free-gift-selector')
        : null;

      return snapshotFromSelector(refreshed || selector);
    } catch (error) {
      return snapshotFromSelector(selector);
    }
  }

  document.addEventListener('click', async (event) => {
    const link = event.target.closest?.('.watc-gift-drawer-link');
    if (!link) return;

    event.preventDefault();
    if (link.disabled) return;

    const selector = link.nextElementSibling;
    if (!selector?.matches('watc-free-gift-selector')) return;

    link.disabled = true;

    try {
      const snapshot = await getFreshDrawerGiftSnapshot(selector);
      if (!link.isConnected) return;

      const state = snapshot?.state;
      if (
        state?.eligible &&
        !state.threeForTwoActive &&
        !state.otherProductDiscountActive
      ) {
        showGiftModal(snapshot.selectorHtml);
      } else {
        selector.dataset.eligible = String(state?.eligible === true);
        selector.dataset.threeForTwoActive = String(
          state?.threeForTwoActive === true
        );
        selector.dataset.otherProductDiscountActive = String(
          state?.otherProductDiscountActive === true
        );
        updateDrawerGiftLink();
      }
    } finally {
      if (link.isConnected) link.disabled = false;
    }
  });

  const drawerGiftObserver = new MutationObserver(updateDrawerGiftLink);
  drawerGiftObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [
      'data-eligible',
      'data-selected-gift-key',
      'data-three-for-two-active',
      'data-other-product-discount-active'
    ]
  });

  document.addEventListener('cart:update', updateDrawerGiftLink);
  document.addEventListener(GIFT_UPDATED_EVENT, updateDrawerGiftLink);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateDrawerGiftLink, {
      once: true
    });
  } else {
    updateDrawerGiftLink();
  }  

})();
